const _ = require("lodash");

const Util = require("../util/ChapterPathUtil");
const ErrorUtil = require("../../../shared/ErrorUtil");
// TODO: refactor to not depend on Derived
const Derived = require("./Derived");
const OpsLibrary = require("../../library/vault/OpsLibrary");

const ChapterSection = require("../../../shared/ChapterSection");
const GradeChapter = require("../../../shared/GradeChapter");
const MiscBlockUtil = require("../../../shared/MiscBlockUtil");
const OpsUser = require("./OpsUser");
const PageShared = require("../../../shared/PageShared");
const TrainerCore = require("../../../shared/TrainerCore");
const {
	atBlockForUser,
	atChapter,
	atChapterPath,
	atCurrChapter,
	atDemoData,
	atLibraryChapter,
	atPage,
	atPageUser,
	atUser,
	atUserAssignedBlocks,
} = require("./StateCursors");
const {setIn} = require("../../../shared/ImmHelpers");
const URLMake = require("../../../shared/URLMake");

// ChapterPath: {
//  animationTriggers,
// 	avatar: {
// 		gradeNumber,
// 		chapterID,
// 		roomIndex,
// 		isUpstairs,
// 	},
//  avatarOverrideContext: Object || null
// 	rooms: {
// 		downstairs,
// 		upstairs,
// 	},
// }

/**
 * The avatarOverrideContext is to store context that helps us determine what the avatar
 * location *should* be. The reason we need to store this context (temporarily) is that
 * we don't necessarily have all the data at the time of calculation (e.g. the chapter
 * model when changing to an unloaded chapter), so we need to store the context to try
 * again later.
 */

const init = () => ({
	animationTriggers: [],
	/**
	 * By default, this is determined by the user's last played data,
	 * but it can be overridden if the user clicks on various buttons
	 * in the chapter path.
	 * Note that `roomIndex` is the index of the element inside the
	 * corresponding rooms array, which also contains stairs.
	 */
	avatar: {
		gradeNumber: null,
		chapterID: null,
		roomIndex: null,
		isUpstairs: null,
	},
	avatarOverrideContext: null,
	rooms: {
		downstairs: [],
		upstairs: [],
	},
});

/**
 * Determines whether an avatar location has been set. Note that we
 * should only check roomIndex and isUpstairs, because the other data
 * may not exist when we're in the demo.
 *
 * @param {Object} chapterPath
 * @returns {Boolean}
 */
const avatarExists = chapterPath => {
	const {roomIndex, isUpstairs} = chapterPath.avatar || {};
	return !_.isNil(roomIndex) && !_.isNil(isUpstairs);
};

/**
 * Checks to see if any avatar overriding context is in the vault.
 *
 * @param {Object} chapterPath
 * @returns {Boolean}
 */
const avatarOverrideContextExists = chapterPath =>
	!_.isEmpty(chapterPath.avatarOverrideContext);

/**
 * Post-update hook
 */
const updateChapterPathData = (vault, oldState, newState, action) => {
	const page = atPage(newState).get();
	const {demo} = page;
	if (!demo) {
		const userModel = atUser(newState).getIn("model");
		if (!userModel) return newState;
	}

	// note that we can calculate avatar data without being on a chapter page
	// If the hallway URL chapter (e.g. from "Start next chapter") differs from the
	// stored avatar, queue enter-chapter so getAvatarData can resolve (must be on state
	// before getAvatarData runs — it reads vault, not the in-flight setter callback).
	let stateForAvatar = newState;
	if (!demo && page.name === "trainer.chapter") {
		const currChapter = atCurrChapter(newState).get();
		const cp = atChapterPath(newState).get();
		if (
			currChapter &&
			currChapter.load === "done" &&
			avatarExists(cp) &&
			cp.avatar &&
			cp.avatar.chapterID != null &&
			currChapter.chapterID !== cp.avatar.chapterID
		) {
			stateForAvatar = atChapterPath(newState).set(chapterPath =>
				updateAvatarOverrideContext(chapterPath, {
					navAction: "enter-chapter",
					chapterID: currChapter.chapterID,
				})
			);
		}
	}
	newState = atChapterPath(stateForAvatar).set(chapterPath => {
		// calculate new avatar data if we are either missing avatar data or have context to calculate with
		const needAvatar =
			!avatarExists(chapterPath) || avatarOverrideContextExists(chapterPath);
		if (needAvatar) {
			const avatarData = getAvatarData(stateForAvatar);
			if (avatarData) {
				chapterPath = setIn(chapterPath, "avatar", avatarData);
				// clear the context after a successful calculation, in case there was any
				chapterPath = updateAvatarOverrideContext(chapterPath, null);
			}
		}
		return chapterPath;
	});
	const chapter = atCurrChapter(newState).get();

	// subsequent calculations require chapter data, so exit here if we don't have them
	if (!demo && _.get(chapter, "load") !== "done") return newState;

	newState = atChapterPath(newState).set(chapterPath => {
		// calculate rooms
		const rooms = getRooms(newState, {chapterID: chapter?.chapterID, demo});
		chapterPath = setIn(chapterPath, "rooms", rooms);

		// calculate animations
		const newPage = atPage(newState).get();
		const oldPage = atPage(oldState).get();
		const oldChapterPath = atChapterPath(oldState).get();
		const newTriggerData = Util.getAnimationTriggerData(chapterPath, newPage);
		const oldTriggerData = Util.getAnimationTriggerData(
			oldChapterPath,
			oldPage
		);
		let animateFrom = chapterPath.animateFrom || {};
		const pageInfo = {
			name: animateFrom.pageName,
			gradeNumber: animateFrom.gradeNumber,
			chapterIndex: animateFrom.chapterIndex,
		};
		const shouldUpdateAnimateFromNow = (() => {
			// Reset animateFrom when we first load
			if (_.isEmpty(animateFrom)) {
				return true;
			}
			const parts1 = PageShared.getNameParts(pageInfo);
			const parts2 = PageShared.getNameParts(page);
			// Reset animateFrom when switching in from theatre or other pages
			if (parts1[0] !== parts2[0]) {
				return true;
			}
			return false;
		})();
		const shouldUpdateAnimateFromNext = (() => {
			if (shouldUpdateAnimateFromNow) {
				return false;
			}

			if (demo) {
				return false;
			}

			// Reset animateFrom when we enter a new chapter e.g. by chapter buttons or chapter modal menu
			if (
				pageInfo.gradeNumber !== page.gradeNumber ||
				pageInfo.chapterIndex !== page.chapterIndex
			) {
				return true;
			}

			if (newPage.showBook && !oldPage.showBook) {
				return true;
			}

			return false;
		})();

		if (shouldUpdateAnimateFromNow) {
			chapterPath = setIn(chapterPath, "animateFrom", newTriggerData);
			animateFrom = chapterPath.animateFrom;
		}
		const animateStep = oldTriggerData;
		const animateTo = newTriggerData;
		const newTriggers = Util.computeVaultAnimationTriggers(
			animateFrom,
			animateStep,
			animateTo
		);
		chapterPath = setIn(chapterPath, "animationTriggers", animationTriggers =>
			animationTriggers.concat(newTriggers)
		);
		const canAnimate = Util.canAnimate(newState);
		chapterPath = setIn(chapterPath, "canAnimate", canAnimate);
		if (canAnimate && chapterPath.animationTriggers.length > 0) {
			chapterPath = setIn(chapterPath, "animateFrom", animateTo);
			chapterPath = setIn(chapterPath, "animating", true);
		}
		if (shouldUpdateAnimateFromNext) {
			chapterPath = setIn(chapterPath, "animateFrom", animateTo);
		}

		return chapterPath;
	});

	return newState;
};

/**
 * Returns the avatar data, dependent on any overriding contexts that may be set
 *
 * @param {Object} vaultState
 * @param {Object} opts
 * 	- getChapterID: if true, just return the chapterID, which doesn't need chapter data to calculate
 * @returns {Object|Number}
 */
const getAvatarData = (vaultState, opts) => {
	const context = atChapterPath(vaultState).getIn("avatarOverrideContext");
	if (context?.navAction) {
		return getAvatarDataForNavigation(vaultState, opts);
	}
	if (context?.forLastPlayed) {
		return getAvatarDataForLastPlayed(vaultState, opts);
	}
	// if no relevant context, calculate for default
	return getAvatarDataForDefault(vaultState, opts);
};

/**
 * Primary function to set avatar location based on certain contexts
 *
 * @param {Object} chapterPath
 * @param {Object} context A group of attributes that help describe where the
 * avatar should go.
 */
const updateAvatarOverrideContext = (chapterPath, context = null) => {
	return setIn(chapterPath, "avatarOverrideContext", context);
};

/**
 * NOTE: "default" is different for Academy students. If you want to guarantee the
 * avatar position based on the last played room, use getAvatarDataForLastPlayed().
 *
 * @param {Object} vaultState
 * @param {Object} opts
 * 	- getChapterID: if true, just return the chapterID, which doesn't need chapter data to calculate
 * @returns {Object|Number}
 */
const getAvatarDataForDefault = (vaultState, opts) => {
	const user = atPageUser(vaultState).get();
	const isAcademy = OpsUser.isAcademyUser(user);
	return isAcademy
		? getAvatarDataForAcademy(vaultState, opts)
		: getAvatarDataForLastPlayed(vaultState, opts);
};

/**
 * Determines where the avatar should be based on the last played room
 *
 * @param {Object} vaultState
 * @param {Object} opts
 * 	- getChapterID: if true, just return the chapterID, which doesn't need chapter data to calculate
 * @returns {Object|Number}
 */
const getAvatarDataForLastPlayed = (vaultState, opts) => {
	const {getChapterID} = opts || {};
	const demo = atPage(vaultState).getIn("demo");
	const user = atPageUser(vaultState).get();
	const {
		room: lastPlayedRoom,
		chapterID: lastPlayedChapterID,
	} = getLastPlayedData(user);

	let avatarRoom = null;
	if (demo) {
		if (getChapterID) {
			return null;
		}
		if (_.isEmpty(lastPlayedRoom)) {
			// if no last played room in demo, use the default data
			const avatarData = atDemoData(vaultState).getIn(
				"chapterPathDefaultAvatar"
			);
			return avatarData;
		} else {
			const completed = _.get(lastPlayedRoom, "completed", null);
			const demoRooms = getDemoRooms(vaultState).downstairs;
			for (let i = 0; i < demoRooms.length; i++) {
				const room = demoRooms[i];
				if (Util.isSameRoom(room, lastPlayedRoom)) {
					const passed = Util.roomIsComplete(room, lastPlayedRoom.isTrophy);
					const index = i + (completed && passed ? 1 : 0);
					avatarRoom = demoRooms[index];
					break;
				}
			}
		}
	} else {
		// non-demo
		if (getChapterID) {
			// if we just want the chapter ID, return that now
			return lastPlayedChapterID;
		}

		const chapter = atChapter(vaultState, lastPlayedChapterID).get();
		if (_.get(chapter, "load") !== "done") return null;

		const allRooms = getRooms(vaultState, {chapterID: lastPlayedChapterID});
		const {blockID, bookSectionID} = lastPlayedRoom;
		if (_.isNil(blockID) && _.isNil(bookSectionID)) {
			// if no data, put before the first lesson of last-played chapter
			avatarRoom = allRooms.downstairs[0]; // default to first room
			for (const room of allRooms.downstairs) {
				if (!Util.blockIsStaircase(room)) {
					if (!Util.roomIsComplete(room)) {
						avatarRoom = room;
						break;
					}
				}
			}
		} else {
			// if there is data, search for that room
			const completed = _.get(lastPlayedRoom, "completed", null);

			for (const rooms of Object.values(allRooms)) {
				for (const [i, room] of Object.entries(rooms)) {
					// exit if already found the room
					if (avatarRoom) break;

					// don't check staircases
					if (Util.blockIsStaircase(room)) continue;

					if (Util.isSameRoom(room, lastPlayedRoom)) {
						if (room.isReading) {
							// demo needs to have the corresponding library chapter loaded
							if (demo) {
								const libraryChapter = atLibraryChapter(
									vaultState,
									room.chapterID
								).get();
								if (libraryChapter?.load !== "done") {
									return null;
								}
							}
						}

						const isUpstairs = Util.blockIsUpstairs(room);
						const passed = Util.roomIsComplete(room, lastPlayedRoom.isTrophy);
						const roomIndex = (() => {
							let index = parseInt(i) + (completed && passed ? 1 : 0);
							if (isUpstairs) {
								// upstairs, the avatar should bounce back to just before the last lesson
								if (!rooms[index] || Util.blockIsStaircase(rooms[index]))
									--index;
							} else {
								// downstairs, the avatar should advance past the stairs
								if (Util.blockIsStaircase(rooms[index])) ++index;
							}
							return index;
						})();
						// NOTE: we'll never have two staircases in a row, so we only have to check once
						avatarRoom = rooms[roomIndex];
					}
				}
			}
		}

		if (!avatarRoom) {
			// this *should* only happen if every single chapter is locked
			if (process.env.NODE_ENV !== "production") {
				ErrorUtil.log("avatar room is falsy", {
					avatarRoom,
					demo,
					lastPlayedRoom,
					allRooms,
				});
			}
			return null;
		}
	}

	// get corresponding index - doing this separately ensures the index corresponds to the correct chapter
	return buildAvatarDataForRoom(vaultState, avatarRoom);
};

/**
 * Determines where the avatar should be (if anywhere) for an Academy student,
 * based on the user's assigned homework data. Specifically, the avatar should
 * go to the **first current unfinished** homework, if one exists.
 *
 * @param {Object} vaultState
 * @param {Object} opts
 * 	- getChapterID: if true, just return the chapterID, which doesn't need chapter data to calculate
 * @returns {Object|Number}
 */
const getAvatarDataForAcademy = (vaultState, opts) => {
	const {getChapterID} = opts || {};
	const user = atPageUser(vaultState).get();
	const isAcademy = OpsUser.isAcademyUser(user);
	if (isAcademy) {
		const assignedBlocks = atUserAssignedBlocks(vaultState).get();
		const unfinishedHomework = MiscBlockUtil.getUnfinishedBlocks(
			assignedBlocks
		);
		if (unfinishedHomework.length > 0) {
			const sortedUnfinishedHomework = MiscBlockUtil.sortUnfinishedHomework(
				unfinishedHomework
			);
			const latestHomework = sortedUnfinishedHomework[0];
			const {isCurrentWeek} = OpsUser.getHomeworkDataForBlock(
				user,
				latestHomework
			);
			if (isCurrentWeek) {
				if (getChapterID) {
					// if we just want the chapter ID, return that now
					return latestHomework.chapterID;
				}
				const chapter = atChapter(vaultState, latestHomework.chapterID).get();
				if (chapter?.load !== "done") {
					// we can't proceed if we don't have chapter data
					return null;
				}
				// if an Academy has any *current* unfinished homework, place avatar at that room
				return buildAvatarDataForRoom(vaultState, latestHomework);
			}
		}
	}
	return getAvatarDataForLastPlayed(vaultState, opts);
};

/**
 * Supported action events:
 * - enter-chapter
 * - next-chapter (same behavior enter-chapter)
 * - previous-chapter
 * - take-stairs
 * - enter-block (only for demo)
 *
 * @param {Object} vaultState
 * @param {Object} opts
 * 	- getChapterID: if true, just return the chapterID, which doesn't need chapter data to calculate
 * @returns {Object|Number}
 */
const getAvatarDataForNavigation = (vaultState, opts) => {
	const context =
		atChapterPath(vaultState).getIn("avatarOverrideContext") || {};
	const {getChapterID} = opts || {};
	const {navAction, chapterID} = context;
	if (!navAction) {
		return null;
	}
	if (getChapterID) {
		// if we just want the chapter ID, return that now
		return chapterID;
	}

	const avatarData = _.clone(atChapterPath(vaultState).getIn("avatar")) || {};

	const demo = atPage(vaultState).getIn("demo");
	if (demo) {
		// note that the demo doesn't need a chapter model here
		if (["enter-block"].includes(navAction)) {
			// NOTE: this is only needed for the demo only
			const {blockID, isTest} = context;
			if (!_.isNil(blockID)) {
				_.extend(avatarData, buildAvatarDataForRoom(vaultState, {blockID}));
			} else if (isTest) {
				const demoRooms = getRooms(vaultState, {demo}).downstairs;
				_.extend(avatarData, {
					gradeNumber: null,
					chapterID: null,
					roomIndex: demoRooms.length - 1,
					isUpstairs: false,
				});
			}
		}
	} else {
		// non-demo
		const chapter = atChapter(vaultState, chapterID).get();
		if (chapter?.load !== "done") {
			return null;
		}
		const {gradeNumber} = chapter.model;

		if (["enter-chapter", "next-chapter"].includes(navAction)) {
			// only move avatar there if it's unlocked
			if (Util.buildingIsUnlocked(chapter.model)) {
				_.extend(avatarData, {
					gradeNumber,
					chapterID,
					roomIndex: 0,
					isUpstairs: false,
				});
			}
		} else if (["previous-chapter"].includes(navAction)) {
			const allRooms = getRooms(vaultState, {chapterID});
			const rooms = allRooms.downstairs;
			const testIsComplete = Util.blockIsComplete(_.last(rooms));
			_.extend(avatarData, {
				gradeNumber,
				chapterID,
				isUpstairs: false,
				roomIndex: testIsComplete
					? rooms.length - 1 // after test room
					: rooms.length - 2, // on (before) test room
			});
		} else if (["take-stairs"].includes(navAction)) {
			const {stairIndex, isUpstairs} = context;
			const allRooms = getRooms(vaultState, {chapterID});
			const rooms = Util.getRoomsForFloor(allRooms, isUpstairs);
			const roomIndex = Util.getRoomIndexForStairIndex(rooms, stairIndex);
			// if upstairs, go to the room after the staircase
			_.extend(avatarData, {
				gradeNumber,
				chapterID,
				roomIndex: roomIndex + (isUpstairs ? 1 : 0),
				isUpstairs: context.isUpstairs,
			});
		}
	}

	return avatarData;
};

/**
 * Builds avatar room metadata for a given room.
 *
 * @param {Object} vaultState
 * @param {Object} room
 * @returns {Object}
 */
const buildAvatarDataForRoom = (vaultState, room) => {
	if (!room) return null;

	const isUpstairs = TrainerCore.isBonusBlock(room);
	const {chapterID} = room;
	const demo = atPage(vaultState).getIn("demo");
	const allRooms = getRooms(vaultState, {chapterID, demo});
	const rooms = Util.getRoomsForFloor(allRooms, isUpstairs);
	const roomIndex = rooms.findIndex(r => Util.isSameRoom(r, room));
	const roomData = rooms[roomIndex];
	if (!roomData) {
		ErrorUtil.log("roomData is falsy", {
			rooms,
			room,
			roomIndex,
			roomData,
		});
	}
	return {
		gradeNumber: roomData?.gradeNumber,
		chapterID: roomData?.chapterID,
		roomIndex,
		isUpstairs,
	};
};

/**
 * Retrieves relevant last played data necessary for the chapter path
 *
 * @param {Object} user
 * @returns {Object}
 */
const getLastPlayedData = user => {
	const lastPlayedData = PageShared.getLastPlayedDataFromUser(user);
	// these are *not* guaranteed
	const lastPlayedRoom = _.get(lastPlayedData, "room", {});
	let {chapterID = null} = lastPlayedRoom;

	if (_.isNull(chapterID)) {
		// these should be guaranteed
		const {gradeNumber, chapterIndex} = lastPlayedData;
		chapterID = GradeChapter.toChapterID(gradeNumber, chapterIndex);
	}

	return {
		room: lastPlayedRoom || {}, // not guaranteed
		chapterID: chapterID !== null ? parseInt(chapterID) : 1, // guaranteed, fall back to chapterID: 1
	};
};

const getDemoRooms = vaultState => {
	if (!vaultState.tempUser.blocks) {
		return {downstairs: null, upstairs: null};
	}

	const blockIDs = [];
	const demoData = atDemoData(vaultState).get();
	if (demoData?.load !== "done") {
		return {downstairs: null, upstairs: null};
	}
	_.forEach(demoData.gradeChapterBlockMapping, grade => {
		grade.map(chapter => blockIDs.push(chapter.blockID));
	});

	const mainBlocks = blockIDs.map(id =>
		atBlockForUser(vaultState, null, id).get()
	);

	const mainBlockModels = mainBlocks.filter(Boolean).map(block => block.model);

	// note that demo currently doesn't have an upstairs
	const rooms = Util.calculateRooms(mainBlockModels, {});

	const demoTest = {
		blockID: -1,
		blockType: "test",
		displayName: "Test",
		unlocked: true,
		progressLock: "unlocked",
	};
	rooms.downstairs.push(demoTest);
	return rooms;
};

const getRooms = (vaultState, {chapterID, demo}) => {
	if (demo) {
		return getDemoRooms(vaultState);
	}

	const blockModels = getBlockModels(vaultState, chapterID);
	if (blockModels.length === 0) return [];

	const auxBlocks = Derived.getChapterAuxBlocks(vaultState, chapterID);
	const rooms = Util.calculateRooms(blockModels, auxBlocks);
	return rooms;
};

const getBlockModels = (vaultState, chapterID) => {
	const auxBlocks = Derived.getChapterAuxBlocks(vaultState, chapterID);
	const sectionBlockMap = Derived.getSectionBlockMapForChapter(
		vaultState,
		chapterID
	);
	const sectionIDs = [0].concat(ChapterSection.getChapterSections(chapterID));
	const {gradeNumber, chapterIndex} = GradeChapter.chapterIDToContext(
		chapterID
	);
	const chapter = atChapter(vaultState, chapterID).get();
	const chapterIsUnlocked = _.get(chapter, "model.unlocked", false);

	let blocks = [];
	_.each(sectionIDs, sectionID => {
		const sectionBlocks = sectionBlockMap[sectionID];

		if (sectionID !== 0) {
			// insert book chapter block
			const data = ChapterSection.getSectionData(sectionID);
			const testBlock = _.get(auxBlocks, ["test", "model"], null);
			const libraryChapter = atLibraryChapter(vaultState, chapterID).get();
			const percentRead = OpsLibrary.percentLibrarySectionRead(
				sectionID,
				libraryChapter
			);
			const fauxBlock = {
				blockStyleType: data.character,
				bookSectionID: sectionID,
				chapterID,
				chapterIndex,
				displayName: data.name,
				gradeNumber,
				isComplete: percentRead === 1,
				isReading: true,
				percentComplete: percentRead,
				requirementLevel: Util.getRequirementLevelForReading(
					sectionID,
					sectionBlockMap
				),
				unlocked: Util.readingIsUnlocked(
					sectionID,
					sectionIDs,
					sectionBlockMap,
					testBlock,
					chapterIsUnlocked
				),
			};
			blocks.push(fauxBlock);
		}

		blocks = blocks.concat(sectionBlocks);
	});

	return blocks;
};

const clearAnimations = chapterPath => {
	if (chapterPath.canAnimate) {
		chapterPath = setIn(chapterPath, "animating", false);
		chapterPath = setIn(chapterPath, "animationTriggers", []);
	}
	return chapterPath;
};

const getChapterIDForAvatar = vaultState => {
	const demo = atPage(vaultState).getIn("demo");
	if (demo) return null;

	const avatarData = atChapterPath(vaultState).getIn("avatar");
	const chapterID = avatarData?.chapterID;
	// if already exists, use that - otherwise, calculate
	return chapterID ?? getAvatarData(vaultState, {getChapterID: true});
};

const getChapterURLForAvatar = vaultState => {
	const demo = atPage(vaultState).getIn("demo");
	if (demo) return URLMake.forDemoChapter();

	const chapterID = getChapterIDForAvatar(vaultState);
	if (chapterID) {
		return URLMake.forChapterFromID(chapterID);
	}
	return null;
};

module.exports = {
	init,
	updateChapterPathData,
	updateAvatarOverrideContext,
	clearAnimations,
	getBlockModels,
	getChapterIDForAvatar,
	getChapterURLForAvatar,
	getLastPlayedData,
};
